import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { cn } from "@/lib/utils";

interface RefreshIndicatorProps {
  /** When the data was last successfully fetched. null = nunca cargado todavía. */
  lastUpdatedAt: Date | null;
  /** True mientras un fetch está en vuelo. */
  refreshing: boolean;
  /** True si el último fetch trajo conversaciones nuevas que no estaban antes. */
  hasNewSinceLast?: boolean;
  /** Callback que dispara un fetch manual. */
  onRefresh: () => void;
  /** Si el último fetch falló, esto es el mensaje. Cambia el estilo del botón a destructivo. */
  errorMessage?: string | null;
}

/**
 * Botón circular de actualización inspirado en el patrón "fresh content" de
 * apps tipo X/Twitter o Linear. Tres estados visuales:
 *
 *   - Idle (estático): "todo al día" — texto secundario, sin pulso
 *   - Refreshing (girando): icono rota mientras hay fetch en vuelo
 *   - Has-new (punto verde): aparece dot pulsante cuando trajo cosas nuevas
 *
 * El tooltip muestra el tiempo relativo desde la última actualización.
 */
export function RefreshIndicator({
  lastUpdatedAt,
  refreshing,
  hasNewSinceLast = false,
  onRefresh,
  errorMessage,
}: RefreshIndicatorProps) {
  // Re-render cada 10s para que el "hace X minutos" se actualice
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const relativeTime = formatRelativeTime(lastUpdatedAt);
  const tooltip = errorMessage
    ? `Error: ${errorMessage}`
    : refreshing
      ? "Actualizando…"
      : lastUpdatedAt
        ? `Actualizado ${relativeTime}`
        : "Aún no se ha cargado";

  return (
    <button
      onClick={onRefresh}
      disabled={refreshing}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        "group relative inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all",
        errorMessage
          ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15"
          : refreshing
            ? "border-brand/30 bg-brand/5 text-brand"
            : hasNewSinceLast
              ? "border-success/40 bg-success/10 text-success hover:bg-success/15"
              : "border-border bg-surface-2 text-muted-foreground hover:border-border-strong hover:text-foreground"
      )}
    >
      <RefreshCw
        className={cn(
          "size-3 transition-transform",
          refreshing && "animate-spin"
        )}
      />
      <span className="tabular-nums">
        {refreshing ? "Actualizando…" : relativeTime}
      </span>

      {/* Dot pulsante cuando hay cosas nuevas y no está refresheando */}
      {hasNewSinceLast && !refreshing && !errorMessage && (
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
          <span className="relative inline-flex size-1.5 rounded-full bg-success" />
        </span>
      )}
    </button>
  );
}

/** Devuelve "ahora", "hace 23s", "hace 4 min", "hace 1h 12m", etc. */
function formatRelativeTime(date: Date | null): string {
  if (!date) return "—";
  const now = Date.now();
  const diff = Math.floor((now - date.getTime()) / 1000);  // segundos

  if (diff < 5) return "ahora";
  if (diff < 60) return `hace ${diff}s`;

  const min = Math.floor(diff / 60);
  if (min < 60) return `hace ${min} min`;

  const hr = Math.floor(min / 60);
  const remainingMin = min % 60;
  if (hr < 24) return remainingMin > 0 ? `hace ${hr}h ${remainingMin}m` : `hace ${hr}h`;

  const days = Math.floor(hr / 24);
  return `hace ${days}d`;
}
